import { useState, useEffect } from 'react';
import AutocompleteInput, { Option } from '@/components/inputs/AutocompleteInput';
import { useAssetModelsOptions } from '../hooks';
import { GlobalButton } from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { Add as AddIcon } from '@mui/icons-material';
import { AddAssetModelModal } from './index';

interface AssetModelAutocompleteInputProps {
  name: string;
  placeholder: string;
  value?: Option | null;
  onChange: (value: Option | null) => void;
  onInputChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  errorInfo?: string;
  autoFocus?: boolean;
  showCreateButton?: boolean;
  brandId?: string;
  minWidth?: string;
}

const AssetModelAutocompleteInput = ({
  name,
  placeholder,
  value,
  onChange,
  onInputChange,
  required = false,
  disabled = false,
  error = false,
  errorInfo = '',
  autoFocus = false,
  showCreateButton = false,
  brandId,
  minWidth = 'min-w-[250px]',
}: AssetModelAutocompleteInputProps) => {
  const [modelSearch, setModelSearch] = useState('');
  const [showAddAssetModelModal, setShowAddAssetModelModal] = useState(false);

  const { assetModelOptions, isLoading } = useAssetModelsOptions({
    search: modelSearch,
    brandId: disabled ? undefined : brandId,
  });

  useEffect(() => {
    if (disabled) {
      setModelSearch('');
    }
  }, [disabled]);

  const handleInputChange = (inputValue: string) => {
    setModelSearch(inputValue);
    onInputChange(inputValue);
  };

  const handleAddAssetModelSuccess = (newModel: {
    id: string;
    name: string;
  }) => {
    const newOption: Option = {
      label: newModel.name,
      data: newModel.id,
    };
    onChange(newOption);
    setModelSearch(newModel.name);
  };

  return (
    <div className={`flex flex-col gap-2 ${minWidth} w-full`}>
      <div className={minWidth}>
        {modelSearch.length > 0 && modelSearch.length < 3 && (
          <div className='text-gray-500 text-xs absolute -mt-5'>
            Escribe al menos 3 letras para buscar modelos.
          </div>
        )}
        <AutocompleteInput
          name={name}
          placeholder={placeholder}
          value={value}
          inputValue={value ? value.label : modelSearch}
          onChange={onChange}
          onInputChange={handleInputChange}
          options={assetModelOptions}
          required={required}
          disabled={disabled}
          loading={isLoading}
          error={error}
          errorInfo={errorInfo}
          autoFocus={autoFocus}
        />
      </div>
      {showCreateButton && (
        <div className='w-full [&>button]:w-full'>
          <GlobalButton
            variant={GlobalButtonVariant.secondaryButton}
            action={() => setShowAddAssetModelModal(true)}
            disabled={!!value}
          >
            <AddIcon color='primary' />
            Crear modelo
          </GlobalButton>
        </div>
      )}
      <AddAssetModelModal
        open={showAddAssetModelModal}
        onClose={() => setShowAddAssetModelModal(false)}
        onSuccess={handleAddAssetModelSuccess}
        brandId={brandId}
      />
    </div>
  );
};

export default AssetModelAutocompleteInput;
